import { ApolloCache } from '@apollo/client';

interface UpdateWorkPackageCacheParams {
  cache: ApolloCache<any>;
  workPackageId: string;
  updates: {
    percentageComplete?: number | null;
    earnedHours?: number | null;
    earnedQuantity?: number | null;
  };
}

/**
 * Updates the work package cache with new data without refetching the entire list
 */
export const updateWorkPackageInCache = ({ cache, workPackageId, updates }: UpdateWorkPackageCacheParams) => {
  try {
    // Read all cached work package queries and update each one
    const cacheData = (cache as any).data?.data;
    if (!cacheData) return;

    // Find all work package query cache keys
    const workPackageQueryKeys = Object.keys(cacheData).filter(
      (key) => key.includes('workPackages(') || key === 'ROOT_QUERY.workPackages'
    );

    workPackageQueryKeys.forEach((queryKey) => {
      try {
        const cachedQuery = cacheData[queryKey];
        if (cachedQuery?.items) {
          // Update the items array
          const updatedItems = cachedQuery.items.map((item: any) => {
            if (item?.__ref) {
              // This is a reference, we need to update the actual object
              const workPackageKey = item.__ref;
              if (cacheData[workPackageKey]?.id === workPackageId) {
                const existingWorkPackage = cacheData[workPackageKey];
                const updatedWorkPackage = {
                  ...existingWorkPackage,
                  ...Object.fromEntries(Object.entries(updates).filter(([_, value]) => value !== undefined)),
                };
                // Update the work package object directly in cache
                cacheData[workPackageKey] = updatedWorkPackage;
              }
            } else if (item?.id === workPackageId) {
              // Direct object, update it
              return {
                ...item,
                ...Object.fromEntries(Object.entries(updates).filter(([_, value]) => value !== undefined)),
              };
            }
            return item;
          });

          // If we modified direct objects, update the query
          if (cachedQuery.items.some((item: any) => !item?.__ref && item?.id === workPackageId)) {
            cacheData[queryKey] = {
              ...cachedQuery,
              items: updatedItems,
            };
          }
        }
      } catch (queryError) {
        console.debug('Could not update specific work package query:', queryError);
      }
    });
  } catch (error) {
    // If we can't read the cache, silently fail
    console.debug('Could not update work package cache:', error);
  }
};
